import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer } from 'lucide-react';
import { docExtras } from '../lib/docExtras';

/**
 * Asked just before a sale receipt prints, and ONLY when the sale actually
 * carries extra text. A plain sale never sees this window.
 *
 * Deliberately has NO "remember this choice" — owner's ruling 2026-09-23:
 * "I may print it now, but not later." The receipt goes to the customer, so
 * the answer is a per-print decision, not a setting.
 *
 * Props:
 *   open, onOpenChange
 *   doc        — the sale about to print
 *   formatLabel— "Thermal" / "Dot Matrix", shown so the operator knows which
 *                paper this answer applies to
 *   onConfirm(extras) — extras = { descriptions: bool, note: bool }
 */
export default function PrintExtrasDialog({ open, onOpenChange, doc, formatLabel = '', onConfirm }) {
  const extras = docExtras(doc);
  const [wantDescriptions, setWantDescriptions] = useState(true);
  const [wantNote, setWantNote] = useState(true);

  // Every opening starts from "print everything" — a previous print must not
  // quietly decide this one.
  useEffect(() => {
    if (open) {
      setWantDescriptions(true);
      setWantNote(true);
    }
  }, [open]);

  if (!extras.hasAny) return null;

  const confirm = (descriptions, note) => {
    onOpenChange?.(false);
    onConfirm?.({ descriptions, note });
  };

  const rowClass =
    'flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors hover:bg-slate-50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="print-extras-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Print extras</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 -mt-1">
          This sale has extra text. Choose what to include
          {formatLabel ? <> on the <strong>{formatLabel}</strong> printout</> : ' on the printout'}.
        </p>

        <div className="space-y-2">
          {extras.hasDescriptions && (
            <label className={rowClass} data-testid="print-extras-descriptions">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={wantDescriptions}
                onChange={(e) => setWantDescriptions(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="block text-sm">Item descriptions</span>
                <span className="block text-[11px] text-slate-400 truncate">
                  {extras.descriptions.length} item{extras.descriptions.length > 1 ? 's' : ''} ·{' '}
                  {extras.descriptions.map((d) => `"${d.text}"`).join(', ')}
                </span>
              </span>
            </label>
          )}

          {extras.hasNote && (
            <label className={rowClass} data-testid="print-extras-note">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={wantNote}
                onChange={(e) => setWantNote(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="block text-sm">Sale note</span>
                <span className="block text-[11px] text-slate-400 truncate">"{extras.note}"</span>
              </span>
            </label>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => confirm(false, false)}
            data-testid="print-extras-skip"
          >
            Skip extras
          </Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => confirm(wantDescriptions, wantNote)}
            data-testid="print-extras-print"
          >
            <Printer size={14} className="mr-1.5" />
            Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
